/**
 * Copyright © 2025 Farmlands Co-operative Society Limited. All rights reserved.
 *
 * This document contains confidential information and is the property of Farmlands.
 * Unauthorised reproduction or distribution of any part of this publication is prohibited without written consent.
 */

import { Stack, Typography } from "@mui/material";
import { FC, type JSX } from "react";
import { FormProvider } from "react-hook-form";

import { Campaign, Lookup, LookupValue } from "../../api/types/types";

import CampaignFormCard from "./CampaignFormCard";
import { COMPETITOR_WHITELIST_TYPES } from "./constants";
import { UseCampaignFormReturn } from "./useCampaignForm";

export interface CampaignFormContentProps {
  campaigns: Campaign[];
  responseCodeLookup?: Lookup;
  competitorLookup?: Lookup;
  activityName?: string;
  campaignForm: UseCampaignFormReturn;
}

const CampaignFormContent: FC<CampaignFormContentProps> = ({
  campaigns,
  responseCodeLookup,
  competitorLookup,
  activityName,
  campaignForm,
}): JSX.Element => {
  const {
    methods,
    getFilteredReasonLookup,
    getFilteredTypeLookup,
    handleResponseCodeChange,
    sortedCompetitorOptions,
  } = campaignForm;
  const { watch } = methods;

  const renderCampaignCard = (campaign: Campaign, index: number) => {
    const campaignKey = campaign.campaignId || index.toString();
    const campaignData = watch(campaignKey) || {
      responseCode: "",
      reason: "",
      type: "",
      competitor: "",
      notes: "",
    };
    const showCompetitor = COMPETITOR_WHITELIST_TYPES.includes(campaignData.type);
    const { lookup: filteredReasonLookup, hasReasons } = getFilteredReasonLookup(
      campaignData.responseCode
    );
    const { lookup: filteredTypeLookup, hasTypes } = getFilteredTypeLookup(
      campaignData.responseCode
    );

    return (
      <CampaignFormCard
        key={campaign.campaignId || index}
        campaign={campaign}
        campaignIndex={index}
        campaignKey={campaignKey}
        responseCodeLookup={responseCodeLookup}
        reasonLookup={filteredReasonLookup}
        typeLookup={filteredTypeLookup}
        competitorLookup={competitorLookup}
        sortedCompetitorOptions={sortedCompetitorOptions as LookupValue[]}
        showCompetitor={showCompetitor}
        hasReasons={hasReasons}
        hasTypes={hasTypes}
        onResponseCodeChange={(value) => handleResponseCodeChange(campaignKey, value)}
      />
    );
  };

  return (
    <FormProvider {...methods}>
      <Stack gap={2}>
        {campaigns.length === 0 ? (
          <Typography
            align="left"
            color="text.secondary"
            data-testid="activity-completion-confirmation-message"
          >
            Are you sure you want to complete {activityName ?? "this activity"}?
          </Typography>
        ) : (
          campaigns.map(renderCampaignCard)
        )}
      </Stack>
    </FormProvider>
  );
};

export default CampaignFormContent;
